import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';

const navLinkClasses = ({ isActive }: { isActive: boolean }) =>
  [
    'rounded-lg px-3 py-2 text-sm font-medium transition-colors',
    isActive ? 'bg-brand-50 text-brand-700' : 'text-ink-muted hover:bg-surface-muted hover:text-ink',
  ].join(' ');

export default function Header() {
  const navigate = useNavigate();
  const { session, signOut } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error logging out:', error);
    } finally {
      navigate('/events');
    }
  };

  return (
    <header className="sticky top-0 z-40 border-b border-surface-subtle bg-white/95 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2"
        >
          <img src="/logo_background_removed.png" alt="Sport Sphere" className="h-9 w-auto" />
          <span className="hidden text-lg font-bold text-ink sm:block">Sport Sphere</span>
        </button>

        <nav className="hidden items-center gap-1 md:flex">
          <NavLink to="/events" className={navLinkClasses}>
            Find events
          </NavLink>
          <NavLink to="/event-planner" className={navLinkClasses}>
            Event Planner
          </NavLink>
        </nav>

        {!session ? (
          <div className="flex items-center gap-2">
            <Link
              to="/login"
              className="rounded-xl px-3 py-2 text-sm font-medium text-ink-muted transition-colors hover:bg-surface-muted hover:text-ink"
            >
              Log in
            </Link>
            <Link
              to="/create-user"
              className="rounded-xl bg-brand-500 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600"
            >
              Sign up
            </Link>
          </div>
        ) : (
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen((open) => !open)}
              className="flex items-center gap-2 rounded-xl border border-surface-subtle px-3 py-2 text-sm font-medium text-ink transition-colors hover:bg-surface-muted"
            >
              Account
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {menuOpen && (
              <div className="absolute right-0 mt-2 w-48 overflow-hidden rounded-xl border border-surface-subtle bg-white py-1 shadow-card-hover">
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    navigate('/account-details');
                  }}
                  className="block w-full px-4 py-2 text-left text-sm text-ink hover:bg-surface-muted"
                >
                  Profile
                </button>
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    navigate('/events');
                  }}
                  className="block w-full px-4 py-2 text-left text-sm text-ink hover:bg-surface-muted md:hidden"
                >
                  Search Events
                </button>
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    navigate('/event-planner');
                  }}
                  className="block w-full px-4 py-2 text-left text-sm text-ink hover:bg-surface-muted md:hidden"
                >
                  Event Planner
                </button>
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    handleLogout();
                  }}
                  className="block w-full px-4 py-2 text-left text-sm font-medium text-red-600 hover:bg-red-50"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
